import { useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { SIX_UPDATE_APPLIED_AT } from '@/common/storage/keys'
import { SIX_PWA_EVENT_UPDATE_AVAILABLE } from '@/common/storage/events'

const UPDATE_APPLY_TTL_MS = 10 * 60 * 1000

const waitForServiceWorkerControl = () => {
    if (!('serviceWorker' in navigator)) return Promise.resolve(false)
    if (navigator.serviceWorker.controller) return Promise.resolve(true)

    return new Promise<boolean>((resolve) => {
        let settled = false
        const onControllerChange = () => {
            if (settled) return
            settled = true
            navigator.serviceWorker.removeEventListener('controllerchange', onControllerChange)
            resolve(true)
        }

        navigator.serviceWorker.addEventListener('controllerchange', onControllerChange)

        window.setTimeout(() => {
            if (settled) return
            settled = true
            navigator.serviceWorker.removeEventListener('controllerchange', onControllerChange)
            resolve(!!navigator.serviceWorker.controller)
        }, 10000)
    })
}

export default function AutoApplyUpdate() {
    const location = useLocation()
    const applyInProgressRef = useRef(false)
    const [applying, setApplying] = useState(false)

    const getLastAppliedAt = () => {
        if (typeof sessionStorage === 'undefined') return null

        const value = sessionStorage.getItem(SIX_UPDATE_APPLIED_AT)
        if (!value) return null

        const timestamp = Number(value)
        return Number.isFinite(timestamp) ? timestamp : null
    }

    useEffect(() => {
        const applyIfAllowed = async () => {
            try {
                if (applyInProgressRef.current) {
                    return
                }
                if (!window.__sixPwaUpdateAvailable) {
                    return
                }
                const pathname = location.pathname || '/'
                const depth = pathname.split('/').filter(Boolean).length
                if (depth > 1) return

                const lastAppliedAt = getLastAppliedAt()
                if (lastAppliedAt && Date.now() - lastAppliedAt < UPDATE_APPLY_TTL_MS) {
                    return
                }

                applyInProgressRef.current = true
                setApplying(true)

                const fn = window.sixPwaApplyUpdate
                if (typeof fn === 'function') {
                    const res = await fn(true)
                    if (res) {
                        const controlled = await waitForServiceWorkerControl()
                        if (controlled) {
                            window.__sixPwaAppliedByApp = true
                            try {
                                sessionStorage.setItem(SIX_UPDATE_APPLIED_AT, String(Date.now()))
                            } catch {
                                // ignore
                            }
                            window.location.reload()
                        }
                    }
                }
            } catch {
                // swallow
            } finally {
                applyInProgressRef.current = false
                setApplying(false)
            }
        }

        // run on navigation/mount
        applyIfAllowed()

        // If the registration check in the SW bootstrap missed the update event,
        // proactively check the current registration for a waiting worker and
        // trigger the apply flow. This makes AutoApplyUpdate resilient to timing
        // races where the event was dispatched before the component mounted.
        if ('serviceWorker' in navigator) {
            navigator.serviceWorker
                .getRegistration()
                .then((reg) => {
                    if (reg?.waiting) {
                        // mark the flag so applyIfAllowed will proceed
                        try {
                            window.__sixPwaUpdateAvailable = true
                            window.dispatchEvent(new CustomEvent(SIX_PWA_EVENT_UPDATE_AVAILABLE))
                        } catch {
                            // ignore
                        }
                        void applyIfAllowed()
                    }
                })
                .catch(() => {
                    /* ignore */
                })
        }

        // also react to explicit update-available events
        const onUpdateEvent = () => {
            void applyIfAllowed()
        }
        window.addEventListener(SIX_PWA_EVENT_UPDATE_AVAILABLE, onUpdateEvent)
        return () => window.removeEventListener(SIX_PWA_EVENT_UPDATE_AVAILABLE, onUpdateEvent)
    }, [location.pathname])

    if (!applying) return null

    return (
        <div
            aria-hidden
            style={{
                position: 'fixed',
                bottom: 12,
                left: 12,
                zIndex: 9999,
                background: 'rgba(0,0,0,0.85)',
                color: '#fff',
                padding: '8px 12px',
                borderRadius: 8,
                fontSize: 13,
            }}
        >
            Applying update…
        </div>
    )
}
